<script setup lang="ts">
import { nextTick, ref } from 'vue'
import type { VMenu } from 'vuetify/components'
import type { NavItem } from '@/types/navigation.types'

interface Props {
	modelValue: boolean
	title: string
	children: NavItem[]
	activeChild: string | null
}

defineProps<Props>()

const emit = defineEmits<{
	(e: 'update:modelValue', value: boolean): void
	(e: 'navigate'): void
}>()

// Hover intent: the short open delay lets the cursor sweep down the rail without every
// group popping open; the close delay covers the trip from the rail icon to the panel.
const OPEN_DELAY = 90
const CLOSE_DELAY = 220
// Lifts the panel so its header row is centred on the rail icon: panel padding (6) + border (1),
// less half the height difference between a rail item (40) and the header (34).
const PANEL_LIFT = 4

const menu = ref<VMenu>()

// Picking a row closes the menu, but the hover open-delay Vuetify started when the pointer
// entered the panel may still be pending and would pop it straight back open. Ignore reopen
// requests until that timer has had its chance to fire.
let ignoreReopenUntil = 0

function onRowClick() {
	ignoreReopenUntil = performance.now() + OPEN_DELAY + 50
	emit('navigate')
}

function onMenuToggle(open: boolean) {
	if (open && performance.now() < ignoreReopenUntil) return
	emit('update:modelValue', open)
}

function onActivatorClick(e: MouseEvent) {
	emit('update:modelValue', true)
	// VListItem turns Enter/Space into a synthetic click with detail 0. Move focus into the
	// panel for keyboard users so ArrowUp/Down work at once; a real click leaves focus alone.
	if (e.detail === 0) focusFirstItem()
}

async function focusFirstItem() {
	await nextTick()
	requestAnimationFrame(() => {
		menu.value?.contentEl?.querySelector<HTMLElement>('.nav-flyout__item')?.focus()
	})
}
</script>

<template>
	<!-- submenu: ArrowRight opens and focuses the first row, ArrowLeft/Escape close and
	     return focus to the rail icon. Focus alone doesn't open it, so Tab still walks the rail. -->
	<v-menu
		ref="menu"
		:model-value="modelValue"
		submenu
		open-on-hover
		:open-on-focus="false"
		:open-delay="OPEN_DELAY"
		:close-delay="CLOSE_DELAY"
		location="end top"
		:offset="[0, PANEL_LIFT]"
		transition="nav-flyout"
		:activator-props="{ onClick: onActivatorClick }"
		@update:model-value="onMenuToggle"
	>
		<template #activator="{ props: activatorProps }">
			<slot
				name="activator"
				:props="activatorProps"
			/>
		</template>

		<!-- The transparent left padding bridges the gap to the rail, so the pointer never
		     crosses dead space on its way from the icon to the panel -->
		<div class="nav-flyout">
			<div class="nav-flyout__panel">
				<div class="nav-flyout__header">{{ title }}</div>
				<v-list
					density="compact"
					nav
					bg-color="transparent"
					class="nav-flyout__list"
					:aria-label="title"
				>
					<v-list-item
						v-for="child in children"
						:key="child.title"
						:to="child.to"
						:href="child.href"
						:target="child.external ? '_blank' : undefined"
						:rel="child.external ? 'noopener' : undefined"
						:active="child.title === activeChild"
						:class="{ 'nav-flyout__item--active': child.title === activeChild }"
						:aria-current="child.title === activeChild ? 'page' : undefined"
						class="nav-flyout__item"
						@click="onRowClick"
					>
						<template #prepend>
							<i
								:class="child.icon"
								class="nav-flyout__icon"
							/>
						</template>
						<v-list-item-title class="nav-flyout__label">{{ child.title }}</v-list-item-title>
						<template
							v-if="child.external"
							#append
						>
							<i class="fa-solid fa-arrow-up-right-from-square nav-flyout__external" />
						</template>
					</v-list-item>
				</v-list>
			</div>
		</div>
	</v-menu>
</template>

<style scoped lang="scss">
// Concentric corners: the panel radius is the row radius plus the panel padding
$panel-padding: 6px;
$row-radius: $radius-md;

.nav-flyout {
	// Spans the 16px from the rail item to the rail edge, plus an 8px float gap
	padding-left: 24px;
}

.nav-flyout__panel {
	position: relative;
	min-width: 220px;
	max-width: 280px;
	padding: $panel-padding;
	border-radius: $row-radius + $panel-padding;
	border: 1px solid rgba(255, 255, 255, 0.08);
	// Same backdrop family as the rail, a step lighter so the panel reads as raised above it
	background:
		radial-gradient(ellipse at 0% 0%, rgba($tami-cyan, 0.1) 0%, transparent 55%),
		linear-gradient(160deg, $shell-gradient-end 0%, $tami-navy-light 100%);
	box-shadow:
		0 18px 40px -8px rgba($tami-navy-dark, 0.6),
		0 4px 12px rgba($tami-navy-dark, 0.35),
		inset 0 1px 0 rgba(255, 255, 255, 0.05);
}

.nav-flyout__header {
	display: flex;
	align-items: center;
	min-height: 34px;
	padding: 0 10px;
	margin-bottom: 4px;
	border-bottom: 1px solid rgba(255, 255, 255, 0.08);
	font-size: 0.75rem;
	font-weight: 600;
	letter-spacing: 0.06em;
	text-transform: uppercase;
	color: rgba(255, 255, 255, 0.55);
	white-space: nowrap;
}

.nav-flyout__list {
	padding: 0 !important;
}

.nav-flyout__item {
	position: relative;
	min-height: 36px !important;
	margin: 1px 0 !important;
	padding: 0 10px !important;
	border-radius: $row-radius !important;
	transition: background-color 150ms ease;

	// Vuetify's state overlay is replaced by the explicit hover/active/focus styles below
	:deep(.v-list-item__overlay) {
		display: none;
	}

	// Active accent bar, same language as the expanded sidebar
	&::before {
		content: '';
		position: absolute;
		left: 0;
		top: 22%;
		bottom: 22%;
		width: 3px;
		border-radius: 2px;
		background: $tami-cyan;
		transform: scaleY(0);
		transition: transform 250ms $ease-out-expo;
	}

	&:hover {
		background-color: rgba(255, 255, 255, 0.07);
	}

	&:focus-visible {
		outline: 2px solid rgba($tami-cyan, 0.6);
		outline-offset: -2px;
	}

	&--active {
		background-color: rgba(255, 255, 255, 0.12);

		&::before {
			transform: scaleY(1);
		}
	}
}

.nav-flyout__icon {
	width: 18px;
	margin-right: 10px;
	font-size: 13px;
	text-align: center;
	color: rgba(255, 255, 255, 0.6);
	transition:
		transform 150ms $ease-standard,
		color 150ms ease;
}

.nav-flyout__label {
	font-size: 0.8125rem;
	font-weight: 400;
	color: rgba(255, 255, 255, 0.86);
	white-space: nowrap;
	transition: color 150ms ease;
}

.nav-flyout__item:hover .nav-flyout__icon,
.nav-flyout__item:focus-visible .nav-flyout__icon {
	transform: translateX(2px);
	color: $tami-cyan;
}

.nav-flyout__item:hover .nav-flyout__label,
.nav-flyout__item:focus-visible .nav-flyout__label {
	color: #ffffff;
}

.nav-flyout__item--active .nav-flyout__icon {
	color: $tami-cyan;
}

.nav-flyout__item--active .nav-flyout__label {
	color: #ffffff;
	font-weight: 500;
}

.nav-flyout__external {
	font-size: 10px;
	opacity: 0.5;
}
</style>
